import CalendarToday from '@mui/icons-material/CalendarToday';
import ChatBubbleOutline from '@mui/icons-material/ChatBubbleOutlineOutlined';
import { Alert, Box, Divider, Stack, Tooltip, Typography } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SystemIcon } from '@volvo/vcdk-react/SystemIcon';
import { type ComponentProps, Fragment, useMemo } from 'react';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getUrlServiceAgreementFileDownload } from 'api/ApiUrls';
import { useProductCenterApi } from 'api/ApplicationApi';
import {
	unitInfoQuery,
	unitKeys,
	unitServiceAgreementFilesQuery,
} from 'api/queries';
import UserIcon from 'assets/volvoIcons/People/UserIcon';
import { FileDisplayBase } from 'library/components/controls/files/FileDisplayBase';
import { ServiceAgreementFileRemoveButton } from 'library/components/controls/files/ServiceAgreementFileRemoveButton';
import { getFileType } from 'library/components/controls/files/functions';
import { getLocalizedDateFormat } from 'library/formatters/DateFormatter';
import { selectUser } from 'library/redux/application';

const HOUR_MS = 60 * 60 * 1000;

type GroupedFiles = {
	key: string;
	uploadTime: number | null;
	timestamp: Date | null;
	comment: string | null;
	submittedBy: string | null;
	isPublic: boolean;
	files: ServiceAgreementFileRow[];
};

export type ServiceAgreementFileRow = {
	id: number;
	fileName: string;
	comment: string | null;
	isPublic: boolean;
	sizeBytes: number;
	businessPartnerName: string | null;
	businessPartnerNumber: string | null;
	uploadUtcDate: Date;
};

type ServiceAgreementFilesTableProps = {
	canWrite: boolean;
	canAdmin: boolean;
	unitId: number;
};

const toValidDate = (value: unknown): Date | null => {
	if (value instanceof Date) {
		return Number.isFinite(value.getTime()) ? value : null;
	}
	if (typeof value === 'string' || typeof value === 'number') {
		const date = new Date(value);
		return Number.isFinite(date.getTime()) ? date : null;
	}
	return null;
};

const normalizeComment = (value: string | null | undefined) =>
	(value ?? '').trim();

const getHourBucketStart = (timestampMs: number | null) =>
	timestampMs != null ? Math.floor(timestampMs / HOUR_MS) * HOUR_MS : null;

const getGroupKey = (
	hourBucketMs: number | null,
	comment: string,
	businessPartnerNumber: string | null,
	isPublic: boolean,
	fallbackId: number
) =>
	hourBucketMs != null
		? `${hourBucketMs}|${comment}|${businessPartnerNumber ?? ''}|${isPublic}`
		: `unknown:${fallbackId}`;

type FileDisplaySx = NonNullable<ComponentProps<typeof FileDisplayBase>['sx']>;

const fileDisplaySx: FileDisplaySx = (theme) => ({
	[theme.breakpoints.only('xs')]: {
		flex: '1',
	},
});

const iconAlignmentSx = {
	'.server-file-display_comment-icon, .server-file-display_calendar-icon, .server-file-display_user-icon':
		{
			verticalAlign: 'bottom',
			mr: 1,
		},
} as const;

export const ServiceAgreementFilesTable = ({
	canWrite,
	canAdmin,
	unitId,
}: ServiceAgreementFilesTableProps): React.JSX.Element => {
	const { t } = useTranslation([
		'relocateTo',
		'productIndividual',
		'common',
		'commonButton',
		'component',
	]);
	const { api } = useProductCenterApi();
	const queryClient = useQueryClient();
	const user = useSelector(selectUser);

	const {
		data: serviceAgreementFiles = [],
		isLoading: serviceAgreementFilesLoading,
		isError: serviceAgreementFilesError,
	} = useQuery({
		...unitServiceAgreementFilesQuery(api, unitId),
		enabled: unitId != null,
	});

	const handleDeleted = async () => {
		await Promise.all([
			queryClient.invalidateQueries({
				queryKey: unitKeys.serviceAgreementFiles(unitId),
			}),
			queryClient.invalidateQueries({
				queryKey: unitInfoQuery(api, unitId).queryKey,
			}),
		]);
	};

	const canRemoveFile = (file: ServiceAgreementFileRow) => {
		if (canAdmin) return true;
		return (
			canWrite &&
			file.businessPartnerNumber ===
				user.customer?.customerNumber?.toString()
		);
	};

	// Business requires files to be displayed in logical "upload groups" (shared comment/date/user/visibility).
	// The backend currently does not provide an explicit upload batch/group identifier, so we apply a
	// deterministic client-side heuristic: group by upload time bucketed to the same hour + normalized comment.
	// This is a pragmatic workaround to match the expected UX; it may merge separate uploads made within the
	// same hour using the same comment. If/when the API exposes a proper group id (or upload session id),
	// this logic should be replaced with that source of truth.
	const groupedFiles = useMemo<GroupedFiles[]>(() => {
		const groups = new Map<string, GroupedFiles>();

		for (const file of serviceAgreementFiles) {
			const timestamp = toValidDate(file.uploadUtcDate);
			const uploadTime = timestamp?.getTime() ?? null;
			const hourBucketMs = getHourBucketStart(uploadTime);
			const commentKey = normalizeComment(file.comment);
			const groupKey = getGroupKey(
				hourBucketMs,
				commentKey,
				file.businessPartnerNumber,
				file.isPublic,
				file.id
			);

			const existing = groups.get(groupKey);
			if (existing) {
				existing.files.push(file);
				continue;
			}
			groups.set(groupKey, {
				key: groupKey,
				uploadTime: hourBucketMs,
				timestamp,
				comment: file.comment ?? null,
				submittedBy: file.businessPartnerName ?? null,
				isPublic: file.isPublic,
				files: [file],
			});
		}

		return Array.from(groups.values())
			.sort((a, b) => (b.uploadTime ?? -1) - (a.uploadTime ?? -1))
			.map((group) => ({
				...group,
				files: group.files.slice().sort((a, b) => b.id - a.id),
			}));
	}, [serviceAgreementFiles]);

	let content: React.ReactNode;
	if (serviceAgreementFilesError) {
		content = (
			<Alert severity="warning">
				{t('component:failed-to-load-files')}
			</Alert>
		);
	} else if (serviceAgreementFilesLoading) {
		content = (
			<Typography
				variant="body2"
				sx={{
					color: 'text.secondary',
				}}>
				{t('common:loading-placeholder')}
			</Typography>
		);
	} else if (serviceAgreementFiles.length === 0) {
		content = (
			<Typography
				variant="body2"
				sx={{
					color: 'text.secondary',
				}}>
				{t('component:no-files-uploaded-yet')}
			</Typography>
		);
	} else {
		content = (
			<Box>
				{groupedFiles.map((group, index) => (
					<Fragment key={group.key}>
						{index > 0 && <Divider sx={{ my: 2 }} />}
						<Box
							className="server-file-display_root"
							sx={{
								display: 'grid',
								alignItems: 'center',
								gap: 2,
								gridTemplateAreas: {
									xs: `"files files"
									"comment comment"
									"date date"
									"user visibility"`,
									md: '"files comment date user visibility"',
								},
								gridTemplateColumns: {
									xs: '1fr auto',
									md: '6fr 7fr 2fr 3fr auto',
								},
								...iconAlignmentSx,
							}}>
							<Stack
								direction="column"
								spacing={2}
								sx={{ gridArea: 'files' }}>
								{group.files.map((file) => (
									<Box key={file.id} sx={{ display: 'flex' }}>
										<FileDisplayBase
											sx={fileDisplaySx}
											name={file.fileName}
											url={getUrlServiceAgreementFileDownload(
												file.id
											)}
											type={
												getFileType(file.fileName) ?? ''
											}
										/>
										{canRemoveFile(file) && (
											<ServiceAgreementFileRemoveButton
												serviceAgreementFileId={file.id}
												onDeleted={handleDeleted}
											/>
										)}
									</Box>
								))}
							</Stack>
							<Box
								sx={{
									gridArea: 'comment',
									whiteSpace: 'pre-wrap',
									display: 'inline-flex',
									alignItems: 'center',
								}}>
								{!normalizeComment(group.comment) && (
									<ChatBubbleOutline
										className="server-file-display_comment-icon"
										color="disabled"
									/>
								)}
								<Typography
									component="span"
									sx={{
										ml: normalizeComment(group.comment)
											? 0
											: 1,
									}}>
									{group.comment ?? ''}
								</Typography>
							</Box>
							<Typography sx={{ gridArea: 'date' }}>
								<CalendarToday
									className="server-file-display_calendar-icon"
									color="disabled"
								/>
								{group.timestamp
									? getLocalizedDateFormat(group.timestamp)
									: ''}
							</Typography>
							<Typography sx={{ gridArea: 'user' }}>
								<UserIcon
									className="server-file-display_user-icon"
									color="primary"
									opacity={0.4}
								/>
								{group.submittedBy ?? ''}
							</Typography>
							<Box
								sx={{
									gridArea: 'visibility',
									display: 'flex',
									justifyContent: 'flex-end',
									justifySelf: 'end',
								}}>
								<Tooltip
									arrow={true}
									title={
										group.isPublic
											? t(
													'productIndividual:document-visibility-label-public'
												)
											: t(
													'productIndividual:document-visibility-label-internal'
												)
									}>
									<span>
										<SystemIcon
											icon={
												group.isPublic
													? 'view'
													: 'view-off'
											}
											size={30}
										/>
									</span>
								</Tooltip>
							</Box>
						</Box>
					</Fragment>
				))}
			</Box>
		);
	}

	return <Box>{content}</Box>;
};
